import React, { useEffect, useState, useMemo, useRef } from "react";
import {
    Typography,
    Box,
    IconButton,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Tooltip,
    CircularProgress,
    Grid,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    Radio,
    RadioGroup,
    FormControlLabel,
    TextField
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import * as Yup from "yup";
import { startCase } from "lodash";
import { Formik, Form, FormikHelpers, useField } from "formik";
import toast from "react-hot-toast";
import { InputField } from "../../pro/applicationForm/input";
import { InlineProductSelector, type Product } from "./InlineProductSelector";
import InlineBrandSelector from "./InlineBrandSelector";
import { CustomFieldLabel } from "../../../components/fields/CustomFieldLabel/CustomFieldLabel";
import { isInternal, isBrand, isRMN, canEditDeliveryDate } from "../../../utils";
import { toDay } from "../../../utils/filmWindow";
import { Dayjs } from "dayjs";
import { CustomFieldCalendar } from "../../../components/fields/CustomFieldCalendar";
import { useUser } from "../../../hooks/useUser";
import axiosInstant from "../../../api";
import { handleConflictError } from "../../../utils/errorHandler";
import { ProjectQuestionToggle } from "./ProjectQuestionToggle";
import { CustomSelectField } from "../../pro/applicationForm/customSelect";
import { statesList } from "../../pro/createEditProfile/formFragments/constants";
import { CustomSelectFieldLabel } from "../../pro/applicationForm/customSelectLabel";
import { SlackChannelPicker } from "../../../components/SlackChannelPicker/SlackChannelPicker";
import {
    BRAND_PROJECT_STATUSES,
    BRAND_PROJECT_TDC_SHIPPING_STATUSES,
    BRAND_PROJECT_EXPERT_SHIPPING_STATUSES
} from "../helper";
import { IconInfo } from "../../../utils/icons";
import { AntSwitch } from "../../admin/videos/selectedVideoCardV2/AntSwitchDesign";
import { getBrandOrdersByBrand, pricingLicenses } from "../../../api/endpoints";
import { formatCurrency } from "../../../utils/formatCurrency";
import {
    ACCOUNT_MANAGER_USERTYPE,
    FULFILLMENT_USERTYPE,
    mergeStaffOptions,
    staffPlaceholder,
    useStaffOptions
} from "../../../hooks/useStaffOptions";
import { ContentChannelOption } from "../../../components/Orders/ChannelMultiSelect";
import { ChannelQuantityPicker } from "../../../components/Orders/ChannelQuantityPicker";
import { ConfirmationDialog } from "../../../components/ConfirmationDialog/ConfirmationDialog";

// Mirrors the API's selection lock (selectionLock.js LOCKED_SELECTION_STATUSES):
// from Pre-Production on, the selection's SOWs are committed.
const LOCKED_SELECTION_STATUSES = new Set<string>([
    BRAND_PROJECT_STATUSES.PRE_PRODUCTION,
    BRAND_PROJECT_STATUSES.FILMING,
    BRAND_PROJECT_STATUSES.EDITING,
    BRAND_PROJECT_STATUSES.VERSION_REVIEW_NEEDED,
    BRAND_PROJECT_STATUSES.FINAL_DELIVERED
]);
// Ranked statuses below Pre-Production: the only targets a revert may land on.
const PRE_LOCK_STATUSES = new Set<string>([
    BRAND_PROJECT_STATUSES.PENDING,
    BRAND_PROJECT_STATUSES.BRIEF_PENDING,
    BRAND_PROJECT_STATUSES.BRIEF_REVIEW_NEEDED,
    BRAND_PROJECT_STATUSES.BRIEF_DENIED,
    BRAND_PROJECT_STATUSES.EXPERT_PROPOSALS_NEEDED,
    BRAND_PROJECT_STATUSES.EXPERTS_REVIEW_NEEDED,
    BRAND_PROJECT_STATUSES.EXPERTS_REVIEW_DENIED,
    BRAND_PROJECT_STATUSES.EXPERTS_SELECTION_NEEDED,
    BRAND_PROJECT_STATUSES.SCHEDULING
]);

interface VideoOptionValue {
    id: number;
    video_option_id: number;
    name: string;
    default?: boolean;
    selection?: {
        brand_project_id: number;
        video_option_value_id: number;
        numeric_value: number | null;
    };
}

interface VideoOption {
    id: number;
    name: string;
    type: "additive" | "exclusive" | "value";
    values: VideoOptionValue[];
}

// For additive/exclusive: Set of value IDs
// For value type: Record of valueId -> numeric input
type VideoOptionSelections = Record<number, Set<number> | Record<number, string | number>>;

interface Props {
    setOpen: (open: boolean) => void;
    fetchProjects: (updated?: any) => void;
    project?: {
        id?: string | number;
        title?: string;
        content_type_id?: string;
        can_ship_two?: boolean;
        online_purchase?: boolean;
        over_100_lbs?: boolean;
        experts?: boolean;
        product?: any;
        brands?: any[];
        status?: string;
        tdc_shipping_status?: string;
        expert_shipping_status?: string;
        est_time_set?: number | null;
        desireid?: string | number;
        download_page?: boolean;
        client_feedback_tracker?: string;
        slack_channel?: string;
        address?: string;
        address_2?: string;
        city?: string;
        state?: string;
        zip?: string;
        order_id?: string | number;
        start_date?: string | null;
        earliest_delivery_date?: string | null;
        option_values?: Array<{
            id: number;
            video_option_id: number;
            name: string;
            selection?: {
                brand_project_id: number;
                video_option_value_id: number;
                numeric_value: number | null;
            };
        }>;
        license_key?: string | null;
        account_manager_id?: number | null;
        producer_id?: number | null;
        project_manager_id?: number | null;
        editor_id?: number | null;
    };
    isEdit?: boolean;
}

interface FormValues {
    project_name: string;
    brand_name: string;
    brands: any[];
    city: string;
    state: string;
    product_name: string;
    product_description: string;
    content_type_id: string;
    content_channel_id: string;
    rmn?: string;
    brand?: string;
    brand_description: string;
    order_id: string;
    product?: string;
    status?: string;
    tdc_shipping_status?: string;
    expert_shipping_status?: string;
    est_time_set?: number | null;
    download_page?: boolean;
    experts?: boolean;
    client_feedback_tracker?: string;
    slack_channel?: string;
    earliest_delivery_date?: Dayjs | null;
    address?: string;
    address_2?: string;
    zip?: string;
    license_key?: string | null;
    account_manager_id?: number | null;
    producer_id?: number | null;
    project_manager_id?: number | null;
    editor_id?: number | null;
}

const BubbleInfo = ({ tooltipText }: any) => (
    <Tooltip title={tooltipText} arrow placement="right">
        <Box className="flex items-center cursor-pointer mb-2">
            <IconInfo />
        </Box>
    </Tooltip>
);

const DownloadPageToggle = ({ name }: { name: string }) => {
    const [field, , helper] = useField(name);

    return (
        <Box className="flex gap-3 items-center">
            <Typography
                sx={{
                    color: "var(--Base-900, #444)",
                    fontSize: "15px",
                    fontStyle: "normal",
                    fontWeight: 450,
                    lineHeight: "132%",
                    fontFamily: "CircularStd"
                }}
            >
                Sharable Download Page
            </Typography>
            <AntSwitch checked={!!field.value} onChange={() => helper.setValue(!field.value)} $isLarge />
        </Box>
    );
};

const ExpertsToggle = ({ name }: { name: string }) => {
    const [field, , helper] = useField(name);

    return (
        <Box className="flex gap-3 items-center">
            <Typography
                sx={{
                    color: "var(--Base-900, #444)",
                    fontSize: "15px",
                    fontStyle: "normal",
                    fontWeight: 450,
                    lineHeight: "132%",
                    fontFamily: "CircularStd"
                }}
            >
                Experts
            </Typography>
            <AntSwitch checked={!!field.value} onChange={() => helper.setValue(!field.value)} $isLarge />
        </Box>
    );
};

export default function AddProjectModal({ setOpen, fetchProjects, project, isEdit = false }: Props) {
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
    const [selectedBrand, setSelectedBrand] = useState<any | null>(null);
    const [selectedRmnBrand, setSelectedRmnBrand] = useState<any | null>(null);
    const [contentTypes, setContentTypes] = useState<any[] | null>(null);
    const [updatedProjectData, setUpdatedProjectData] = useState<any | null>(null);
    const [contentTypesLoading, setContentTypesLoading] = useState(false);
    const [rmnBrandOptions, setRmnBrandOptions] = useState<any[]>([]);
    const [regularBrandOptions, setRegularBrandOptions] = useState<any[]>([]);

    const [orders, setOrders] = useState<any[]>([]);
    const [ordersLoading, setOrdersLoading] = useState(false);

    interface LicenseOption {
        key: string;
        display_name: string;
        price: string | number;
    }
    const [licenseOptions, setLicenseOptions] = useState<LicenseOption[]>([]);

    useEffect(() => {
        let cancelled = false;
        axiosInstant
            .get(pricingLicenses)
            .then((res) => {
                if (!cancelled) setLicenseOptions(res.data || []);
            })
            .catch(() => {
                if (!cancelled) setLicenseOptions([]);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const accountManagers = useStaffOptions(ACCOUNT_MANAGER_USERTYPE);
    const fulfillment = useStaffOptions(FULFILLMENT_USERTYPE);
    // PMs come from either team (the default PM is an account manager admin).
    const projectManagers = mergeStaffOptions(fulfillment, accountManagers);

    const [channels, setChannels] = useState<any[]>([]);
    const [channelsLoading, setChannelsLoading] = useState(false);
    const [selectedChannels, setSelectedChannels] = useState<ContentChannelOption[]>([]);
    const [selectedContentTypeId, setSelectedContentTypeId] = useState<number | null>(
        project?.content_type_id ? Number(project.content_type_id) : null
    );

    const [isProcessingQueue, setIsProcessingQueue] = useState(false);
    const contentTypesRequestRef = useRef(0);

    // Video options state
    const [videoOptions, setVideoOptions] = useState<VideoOption[]>([]);
    const [videoOptionsLoading, setVideoOptionsLoading] = useState(false);
    const [videoOptionSelections, setVideoOptionSelections] = useState<VideoOptionSelections>({});

    const { role, user } = useUser();
    // Locked selection (Pre-Production reached): product + content type are
    // committed to the SOWs, so they're frozen here. Channels stay editable.
    const isProcessLocked = isEdit && !!(project as any)?.process_locked;
    // A backward move out of a locked selection is a revert (TDC-1762): the API
    // only takes it with revert_selection, which moves every project on the
    // selection back and voids its SOWs — so it waits on this confirmation.
    const [pendingRevert, setPendingRevert] = useState<{
        values: FormValues;
        actions: FormikHelpers<FormValues>;
    } | null>(null);

    const [canShipTwo, setCanShipTwo] = useState(Boolean(project?.can_ship_two ?? true));
    const [onlinePurchase, setOnlinePurchase] = useState(Boolean(project?.online_purchase ?? true));
    const [isOver100Lbs, setIsOver100Lbs] = useState(Boolean(project?.over_100_lbs ?? true));

    const initialValues: FormValues = useMemo(() => {
        let rmnValue = "";
        let brandValue = "";

        if (isEdit && project?.brands) {
            const projectBrands = project.brands;
            if (projectBrands.length >= 2) {
                rmnValue = projectBrands[0]?.id?.toString() || "";
                brandValue = projectBrands[1]?.id?.toString() || "";
            } else if (projectBrands.length === 1) {
                const brand = projectBrands[0];
                if (brand?.rmn) {
                    rmnValue = brand.id?.toString() || "";
                } else {
                    brandValue = brand.id?.toString() || "";
                }
            }
        } else if (!isEdit && !isInternal(role) && user?.brand?.find((b: any) => b?.rmn)) {
            rmnValue = user.brand.find((b: any) => b?.rmn)?.id?.toString() || "";
        } else if (!isEdit && !isInternal(role) && user?.brand?.find((b: any) => !b?.rmn)?.id) {
            brandValue = user.brand.find((b: any) => !b?.rmn)?.id?.toString() || "";
        }

        const values = {
            project_name: project?.title || "",
            brand_name: project?.brands?.find((brand) => !brand.rmn)?.name || "",
            brands: [],
            city: project?.city || "",
            state: project?.state || "",
            product_name: project?.product?.title || "",
            product_description: "",
            content_type_id: project?.content_type_id || "",
            content_channel_id: "",
            order_id: project?.order_id?.toString() || "",
            rmn: rmnValue,
            brand: brandValue,
            brand_description: "",
            product: "",
            status: project?.status || "",
            tdc_shipping_status: project?.tdc_shipping_status || "Pending",
            expert_shipping_status: project?.expert_shipping_status || "Pending",
            est_time_set: (project as any)?.est_time_set ?? null,
            download_page: project?.download_page || false,
            experts: project?.experts ?? true,
            client_feedback_tracker: project?.client_feedback_tracker || "",
            slack_channel: (project as any)?.slack_channel || "",
            earliest_delivery_date: toDay(project?.earliest_delivery_date),
            address: project?.address || "",
            address_2: project?.address_2 || "",
            zip: project?.zip || "",
            license_key: project?.license_key ?? null,
            account_manager_id: project?.account_manager_id ?? null,
            producer_id: project?.producer_id ?? null,
            project_manager_id: project?.project_manager_id ?? null,
            editor_id: project?.editor_id ?? null
        };

        return values;
    }, [project, isEdit, role, user?.brand]);

    const validationSchema = Yup.object().shape({
        project_name: Yup.string()
            .required("Project name is required")
            .min(3, "Project name must be at least 3 characters")
            .max(100, "Project name must be less than 100 characters"),
        content_type_id: Yup.string().required("Content type is required"),
        rmn: Yup.string().when(["$isInternal", "$hasRmnOptions"], {
            is: (internal: boolean, hasRmnOptions: boolean) =>
                !internal && hasRmnOptions,
            then: Yup.string().test(
                "rmn-selected",
                "Please select a Parent",
                (value) => !!value,
            ),
        }),
        brand: Yup.string().when(["$isInternal", "rmn"], {
            is: (internal: boolean, rmn: string) => internal && !!rmn,
            then: Yup.string()
                .required("Brand selection is required")
                .test("brand-selected", "Please select a brand", (value) => !!value)
        }),
        product_name: Yup.string().when(["$isInternal", "brand"], {
            is: (internal: boolean, brand: string) => internal && !!brand,
            then: Yup.string()
                .required("Product selection is required")
                .test("product-selected", "Please select a product", (value) => !!value)
        })
    });

    const getRMNOptions = async () => {
        let response;
        let options = [];
        // If brand
        if (isBrand(role) && !isRMN(user)) {
            const brandId = user?.brand[0].id;
            // For Brands that are not RMN
            // Get Asscociated RMNs
            try {
                response = await axiosInstant.get(`/brands/${brandId}/associated-rmns`);
                options = response.data.map((brand: any) => ({
                    label: brand.name,
                    value: brand.id.toString()
                }));
            } catch {
                options = [];
            }
        }
        // All other roles (RMN, Admin, AM, Internal)
        else {
            try {
                response = await axiosInstant.get("/brands?rmn=true&limit=100");
                options = response.data.rows.map((brand: any) => ({
                    label: brand.name,
                    value: brand.id.toString()
                }));
            } catch {
                options = [];
            }
        }
        setRmnBrandOptions(options);
    };

    const getBrandOptions = async () => {
        let response;
        let options = [];
        try {
            response = await axiosInstant.get("/brands?rmn=false&limit=100");
            options = response.data.rows.map((brand: any) => ({
                label: brand.name,
                value: brand.id.toString()
            }));
        } catch {
            options = [];
        }
        setRegularBrandOptions(options);
    };

    const getChannels = () => {
        setChannelsLoading(true);
        try {
            if (!selectedContentTypeId || !contentTypes) {
                setChannels([]);
                setChannelsLoading(false);
                return;
            }

            const contentType = contentTypes.find((ct: any) => ct.id === selectedContentTypeId);
            // Generic channels: every content type carries all active
            // channels (no rmn distinction anymore).
            setChannels(contentType?.content_channels || []);
        } catch {
            setChannels([]);
        } finally {
            setChannelsLoading(false);
        }
    };

    const getOrders = async () => {
        const brandId = selectedRmnBrand?.id || selectedBrand?.id;
        if (!brandId || brandId === "new") {
            setOrders([]);
            return;
        }
        setOrdersLoading(true);
        try {
            const response = await axiosInstant.get(getBrandOrdersByBrand(brandId));
            setOrders(response.data || []);
        } catch {
            setOrders([]);
        } finally {
            setOrdersLoading(false);
        }
    };

    const getVideoOptions = async () => {
        setVideoOptionsLoading(true);
        try {
            const response = await axiosInstant.get("/video-options");
            const options = response.data || [];
            setVideoOptions(options);

            // Initialize selections after fetching options
            const currentProject = updatedProjectData || project;
            const initialSelections = initializeSelections(options, currentProject?.option_values);
            setVideoOptionSelections(initialSelections);
        } catch {
            setVideoOptions([]);
        } finally {
            setVideoOptionsLoading(false);
        }
    };

    // Additive: toggle value in set
    const toggleAdditive = (optionId: number, valueId: number) => {
        setVideoOptionSelections((prev) => {
            const current = new Set((prev[optionId] as Set<number>) || new Set());
            if (current.has(valueId)) {
                current.delete(valueId);
            } else {
                current.add(valueId);
            }
            return { ...prev, [optionId]: current };
        });
    };

    // Exclusive: replace set with single value
    const selectExclusive = (optionId: number, valueId: number) => {
        setVideoOptionSelections((prev) => ({
            ...prev,
            [optionId]: new Set([valueId])
        }));
    };

    // Value: update the numeric value for a specific option value
    const updateValueOption = (optionId: number, valueId: number, numericValue: string | number) => {
        setVideoOptionSelections((prev) => ({
            ...prev,
            [optionId]: {
                ...((prev[optionId] as Record<number, string | number>) || {}),
                [valueId]: numericValue
            }
        }));
    };

    // Build the option_value_ids payload for submit
    const buildOptionValueIds = () => {
        const result: (number | { id: number; numeric_value: number })[] = [];

        for (const option of videoOptions) {
            const sel = videoOptionSelections[option.id];
            if (!sel) continue;

            if (option.type === "value") {
                // Include entries where the user has entered a value
                const valueRecord = sel as Record<number, string | number>;
                for (const [valueId, numericValue] of Object.entries(valueRecord)) {
                    if (numericValue !== "" && numericValue !== undefined && numericValue !== null) {
                        result.push({
                            id: parseInt(valueId),
                            numeric_value: parseFloat(String(numericValue))
                        });
                    }
                }
            } else {
                // additive or exclusive — plain IDs
                const valueSet = sel as Set<number>;
                for (const valueId of valueSet) {
                    result.push(valueId);
                }
            }
        }

        return result;
    };

    // Initialize selections from project data or defaults
    const initializeSelections = (options: VideoOption[], optionValues?: any[]) => {
        const selections: VideoOptionSelections = {};

        // Group existing option_values by option id. The API may return this
        // as `video_option_id` or `brand_project_option_id` depending on the
        // endpoint, so we read either.
        const existingByOptionId: Record<number, any[]> = {};
        if (optionValues && optionValues.length > 0) {
            for (const v of optionValues) {
                const groupId = v.video_option_id ?? v.brand_project_option_id;
                if (groupId == null) continue;
                if (!existingByOptionId[groupId]) {
                    existingByOptionId[groupId] = [];
                }
                existingByOptionId[groupId].push(v);
            }
        }

        // Initialize each option
        for (const option of options) {
            const existingForOption = existingByOptionId[option.id] || [];

            if (option.type === "value") {
                // Value type: store numeric values
                selections[option.id] = {};
                for (const v of existingForOption) {
                    const joinData = v.selection;
                    (selections[option.id] as Record<number, string | number>)[v.id] = joinData?.numeric_value ?? "";
                }
            } else {
                // Additive/Exclusive type
                if (existingForOption.length > 0) {
                    // Use existing selections
                    const set = new Set<number>();
                    for (const v of existingForOption) {
                        set.add(v.id);
                    }
                    selections[option.id] = set;
                } else {
                    // No existing selections for this option - apply defaults
                    const set = new Set<number>();
                    for (const value of option.values) {
                        if (value.default) {
                            set.add(value.id);
                        }
                    }
                    selections[option.id] = set;
                }
            }
        }

        return selections;
    };

    const getContentTypes = async () => {
        const requestId = ++contentTypesRequestRef.current;
        let response;
        let options = [];
        setContentTypesLoading(true);

        // If RMN is selected get content types for that RMN
        if (selectedRmnBrand) {
            const brandId = selectedRmnBrand.id;
            try {
                response = await axiosInstant.get(
                    `/brands/${brandId}/content-types?isRmnBrand=true&expertConnect=true`
                );
                options = response.data;
            } catch {
                options = [];
            }
        }
        // Else all content types
        else {
            try {
                response = await axiosInstant.get("/content-types?expertConnect=true");
                options = response.data;
            } catch {
                options = [];
            }
        }
        if (requestId !== contentTypesRequestRef.current) return;
        setContentTypes(options);
        setContentTypesLoading(false);
    };

    useEffect(() => {
        getRMNOptions();
        getBrandOptions();
        getContentTypes();
        getVideoOptions();

        // Reset Form
        if (!isEdit) {
            setSelectedProduct(null);
            setSelectedProducts([]);
            setSelectedChannels([]);
            setSelectedBrand(null);
            setSelectedRmnBrand(null);
            setSelectedContentTypeId(null);
            setChannels([]);
            setCanShipTwo(true);
            setOnlinePurchase(true);
            setIsOver100Lbs(true);
            setUpdatedProjectData(null);
            // Video option selections will be initialized when videoOptions are fetched
        }
        // If editing
        else if ((project || updatedProjectData) && isEdit) {
            const currentProject = updatedProjectData || project;
            setCanShipTwo(Boolean(currentProject.can_ship_two ?? true));
            setOnlinePurchase(Boolean(currentProject.online_purchase ?? true));
            setIsOver100Lbs(Boolean(currentProject.over_100_lbs || true));

            if (currentProject.product) {
                setSelectedProduct(currentProject.product);
            }
            // Populate multi-product state from products array
            if (currentProject.products?.length) {
                setSelectedProducts(currentProject.products);
            } else if (currentProject.product) {
                setSelectedProducts([currentProject.product]);
            }

            // Populate multi-channel state
            if (currentProject.content_channels?.length) {
                setSelectedChannels(currentProject.content_channels);
            }

            const rmnBrand = currentProject.brands?.filter((brand: any) => brand.rmn);
            if (rmnBrand.length > 0) setSelectedRmnBrand(rmnBrand[0]);

            const brand = currentProject.brands?.filter((brnd: any) => !brnd.rmn);
            if (brand.length > 0) setSelectedBrand(brand[0]);

            if (currentProject.content_type_id) {
                setSelectedContentTypeId(Number(currentProject.content_type_id));
            }

            // Video option selections will be re-initialized when videoOptions are fetched
        }

        // If the user is a brand and not internal or RMN, set the brand field
        if (isBrand(role) && !isInternal(role) && !isRMN(user)) {
            setSelectedBrand(
                user?.brand?.find((b: any) => !b?.rmn) || user?.brand?.[0] || null,
            );
        }

        // If the user is a RMN user, set the RMN brand field. Pick the RMN brand
        // explicitly — the brand array has no guaranteed order, so brand[0] may
        // be a non-RMN brand for multi-brand users.
        if (isRMN(user))
            setSelectedRmnBrand(user?.brand?.find((b: any) => b?.rmn) || null);
    }, [user, project, isEdit, updatedProjectData]);

    // In edit mode, fetch the full project by id so relations like
    // option_values (which the list endpoint omits) are available for
    // initializing advanced options selections.
    useEffect(() => {
        if (!isEdit || !project?.id) return;
        let cancelled = false;
        (async () => {
            try {
                const response = await axiosInstant.get(`/brand-projects/${project.id}`);
                if (!cancelled && response?.data) {
                    setUpdatedProjectData(response.data);
                }
            } catch (err) {
                console.error("Failed to fetch project for edit:", err);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isEdit, project?.id]);

    useEffect(() => {
        getContentTypes();
    }, [selectedRmnBrand]);

    useEffect(() => {
        getOrders();
    }, [selectedBrand, selectedRmnBrand]);

    useEffect(() => {
        getChannels();
    }, [selectedContentTypeId, selectedRmnBrand, contentTypes]);

    const scrollToError = () => {
        setTimeout(() => {
            const errorElem = document.querySelector(".input-error");
            if (errorElem) {
                errorElem.scrollIntoView({
                    behavior: "smooth",
                    block: "center"
                });
            }
        }, 200);
    };

    const handleSubmit = async (
        values: typeof initialValues,
        actions: FormikHelpers<typeof initialValues>,
        revertConfirmed = false
    ) => {
        const errors = validateForm(values);
        if (Object.keys(errors).length > 0) {
            Object.entries(errors).forEach(([, message]) => {
                toast.error(message);
            });
            actions.setSubmitting(false);
            scrollToError();
            return;
        }

        const isRevert =
            isEdit &&
            isInternal(role) &&
            (isProcessLocked || LOCKED_SELECTION_STATUSES.has(project?.status || "")) &&
            !!values.status &&
            values.status !== project?.status &&
            PRE_LOCK_STATUSES.has(values.status);
        if (isRevert && !revertConfirmed) {
            actions.setSubmitting(false);
            setPendingRevert({ values, actions });
            return;
        }

        setIsProcessingQueue(true);

        try {
            const rmnBrandId: string | null = selectedRmnBrand?.id || null;

            // Brand creation is owned by the create-brand modal; by submit time the
            // selected brand already has a real numeric id.
            const brandId = selectedBrand?.id || user?.brand?.[0]?.id || null;

            // Product creation is owned by the selector's create-product modal —
            // everything in selectedProducts already exists in the catalog, so
            // submit only LINKS products (desireids), never creates them.
            const productIds = selectedProducts.map((p: any) => p.desireid).filter(Boolean);
            if (selectedProduct?.desireid && !productIds.includes(selectedProduct.desireid)) {
                productIds.push(selectedProduct.desireid);
            }

            const productPayload = productIds.length > 0 ? { desireids: productIds } : {};

            const finalPayload = {
                project_name: values.project_name,
                title: values.project_name,
                ...productPayload,
                brand_id: brandId,
                rmn_brand_id: rmnBrandId || null,
                content_type_id: values.content_type_id || null,
                // Edit sends the instance list so the backend's id-keep diff
                // protects specific instances. Create keeps the legacy shape.
                // Internal status now lives on the deliverable (s3_file) — it is
                // set per-deliverable in the Downloads tab, not here.
                ...(isEdit
                    ? {
                          project_channels: selectedChannels.map((ch) => ({
                              ...(ch.brand_project_channel_id ? { id: ch.brand_project_channel_id } : {}),
                              content_channel_id: ch.id
                          }))
                      }
                    : { content_channel_ids: selectedChannels.map((ch) => ch.id) }),
                order_id: values.order_id ? Number(values.order_id) : null,
                can_ship_two: canShipTwo,
                online_purchase: onlinePurchase,
                over_100_lbs: isOver100Lbs,
                status: values.status || project?.status || "Pending Approval",
                tdc_shipping_status: values.tdc_shipping_status || project?.tdc_shipping_status || "Pending",
                expert_shipping_status: values.expert_shipping_status || project?.expert_shipping_status || "Pending",
                download_page: values.download_page || false,
                ...(isEdit && isInternal(role) ? { experts: values.experts ?? true } : {}),
                est_time_set: values.est_time_set ?? null,
                slack_channel: values.slack_channel || "",
                license_key: values.license_key || null,
                ...(isEdit && values.client_feedback_tracker !== undefined
                    ? {
                          client_feedback_tracker: values.client_feedback_tracker || ""
                      }
                    : {}),
                ...(isEdit && isInternal(role)
                    ? {
                          address: values.address || "",
                          address_2: values.address_2 || "",
                          city: values.city || "",
                          state: values.state || "",
                          zip: values.zip || ""
                      }
                    : {}),
                ...(canEditDeliveryDate(role) && isEdit
                    ? {
                          earliest_delivery_date:
                              values.earliest_delivery_date?.format("YYYY-MM-DD") ?? null
                      }
                    : {}),
                ...(isInternal(role) && isEdit
                    ? {
                          account_manager_id: values.account_manager_id ?? null,
                          producer_id: values.producer_id ?? null,
                          project_manager_id: values.project_manager_id ?? null,
                          editor_id: values.editor_id ?? null
                      }
                    : {}),
                // Build video option selections with proper format
                option_value_ids: buildOptionValueIds(),
                ...(isRevert ? { revert_selection: true } : {})
            };

            let response;
            if (isEdit && project) {
                response = await axiosInstant.patch(`/brands/${brandId}/projects/${project.id}`, finalPayload);
                toast.success("Project updated successfully!");
            } else {
                response = await axiosInstant.post(`/brands/${brandId}/projects`, finalPayload);
                toast.success("Project created successfully!");
            }

            fetchProjects(response?.data);

            if (isEdit && response.data) {
                actions.setSubmitting(false);
                setOpen(false);
            } else {
                setOpen(false);
                actions.resetForm();
                actions.setSubmitting(false);
                setSelectedProduct(null);
                setSelectedProducts([]);
                setSelectedBrand(null);
                setSelectedRmnBrand(null);
                setCanShipTwo(true);
                setOnlinePurchase(true);
                setIsOver100Lbs(true);
            }
        } catch (error: any) {
            console.error("Project creation/update failed:", error);
            console.error("Error details:", {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
                isEdit,
                projectId: project?.id
            });

            if (!handleConflictError(error, actions, "project_name")) {
                toast.error(
                    isEdit
                        ? "Failed to update project. Please try again."
                        : "Failed to create project. Please try again."
                );
            }
            actions.setSubmitting(false);
            scrollToError();
        } finally {
            setIsProcessingQueue(false);
        }
    };

    const handleClose = () => {
        setOpen(false);
    };

    const isValidProduct = (product: any, formValues: any) => {
        const isNew = product?.id === "new";
        if (isNew) return formValues?.product_name.length && formValues?.product_description.length;
        return product?.desireid;
    };

    const isValidBrand = (brand: any) => !!brand?.id;

    const validateForm = (formValues?: any) => {
        const errors: { [key: string]: string } = {};

        if (isInternal(role) && !isValidBrand(selectedBrand)) {
            errors.brand = "Please select a brand to proceed";
        }

        const hasMultipleProducts = selectedProducts.length > 0;
        if (!hasMultipleProducts && !isValidProduct(selectedProduct, formValues)) {
            errors.product = "Please select at least one product to proceed";
        }

        return errors;
    };

    return (
        <Dialog open onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>
                <Box className="flex justify-between items-center">
                    <Typography variant="h5">{isEdit ? "Edit Project" : "New Project"}</Typography>
                    <IconButton onClick={() => setOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                validateOnMount
                validateOnChange
                validateOnBlur
                context={{
                    isInternal: isInternal(role),
                    hasRmnOptions: rmnBrandOptions.length > 0
                }}
            >
                {({ setFieldValue, values, submitForm }) => {
                    const formRef = useRef<HTMLFormElement>(null);

                    // Keep an already-selected product's title in sync with the typed
                    // name. Use a functional update (no stale closure) and never
                    // fabricate a product when none is selected.
                    useEffect(() => {
                        setSelectedProduct((prev) =>
                            prev ? { ...prev, title: values.product_name } : prev
                        );
                    }, [values.product_name]);

                    useEffect(() => {
                        if (!isEdit || !updatedProjectData) return;
                        setFieldValue("account_manager_id", updatedProjectData.account_manager_id ?? null);
                        setFieldValue("producer_id", updatedProjectData.producer_id ?? null);
                        setFieldValue("project_manager_id", updatedProjectData.project_manager_id ?? null);
                        setFieldValue("editor_id", updatedProjectData.editor_id ?? null);
                    }, [updatedProjectData]);

                    const handleRMNChange = (event: any) => {
                        const selectedRmn = rmnBrandOptions.find((opt) => opt.value === event.target.value);
                        setSelectedRmnBrand({
                            id: selectedRmn?.value,
                            name: selectedRmn?.label
                        });
                        setFieldValue("rmn", event.target.value);
                        setFieldValue("content_channel_id", "");
                        setFieldValue("content_type_id", "");
                        setSelectedContentTypeId(null);
                        setSelectedChannels([]);
                    };

                    const scrollToBottom = () => {
                        setTimeout(() => {
                            formRef.current?.scrollTo({
                                top: formRef.current?.scrollHeight,
                                behavior: "smooth"
                            });
                        }, 200);
                    };

                    const contentTypesMemo = useMemo(() => {
                        if (contentTypesLoading) {
                            return <div className="text-gray-500 text-sm">Loading content types...</div>;
                        }
                        if (contentTypes) {
                            if (contentTypes.length) {
                                return (
                                    <CustomSelectField
                                        name="content_type_id"
                                        label="Content Type"
                                        placeholder="Select Content Type"
                                        options={contentTypes.map((ct) => ({
                                            label: startCase(ct.name.replace(/-/gi, " ")),
                                            value: ct.id
                                        }))}
                                        required
                                        noLabel={false}
                                        disabled={isProcessLocked}
                                        inputProps={{
                                            className: "!pl-[14px]"
                                        }}
                                        onChange={(e: any) => {
                                            if (isProcessLocked) return;
                                            const value = e.target.value;
                                            setFieldValue("content_type_id", value);
                                            setSelectedContentTypeId(value ? Number(value) : null);
                                            if (selectedRmnBrand) {
                                                setFieldValue("content_channel_id", "");
                                                setSelectedChannels([]);
                                            }
                                        }}
                                    />
                                );
                            }
                            return (
                                <div className="text-gray-500 text-sm">No content types available for this brand.</div>
                            );
                        }
                        return null;
                    }, [contentTypesLoading, contentTypes, selectedRmnBrand, setFieldValue, isProcessLocked]);

                    const channelsMemo = useMemo(
                        () => (
                            <ChannelQuantityPicker
                                channels={channels}
                                selectedChannels={selectedChannels}
                                onChange={setSelectedChannels}
                                loading={channelsLoading}
                            />
                        ),
                        [channelsLoading, channels, selectedChannels]
                    );

                    const ordersMemo = useMemo(() => {
                        // Non-RMN brand users should not see orders from RMN projects
                        if (isBrand(role) && !isRMN(user) && selectedRmnBrand) {
                            return null;
                        }
                        if (ordersLoading) {
                            return <div className="text-gray-500 text-sm">Loading orders...</div>;
                        }
                        if (orders.length > 0) {
                            return (
                                <CustomSelectField
                                    name="order_id"
                                    label="Order"
                                    placeholder="Select Order"
                                    options={orders.map((o: any) => ({
                                        label: o.name,
                                        value: o.id
                                    }))}
                                    required={false}
                                    noLabel={false}
                                    inputProps={{
                                        className: "!pl-[14px]"
                                    }}
                                />
                            );
                        }
                        return null;
                    }, [ordersLoading, orders, role, user, selectedRmnBrand]);

                    return (
                        <>
                            <Form className="overflow-y-auto" ref={formRef}>
                                <DialogContent className="border-y border-border-token">
                                    <Box display="flex" flexDirection="column" gap={3}>
                                        <InputField
                                            name="project_name"
                                            label="Project Name"
                                            placeholder="Project Name"
                                            required
                                        />

                                        {isInternal(role) && isEdit && (
                                            <CustomSelectFieldLabel
                                                name="status"
                                                label="Project Status"
                                                placeholder="Select Status"
                                                options={Object.entries(BRAND_PROJECT_STATUSES)
                                                    // Cancelling has its own flow (Cancel Project, with a
                                                    // required reason) and the API rejects a Cancelled
                                                    // status without one, so the dropdown never OFFERS
                                                    // it — but it must still DISPLAY it on a project that
                                                    // is already cancelled, or the field renders blank.
                                                    .filter(
                                                        ([key]) =>
                                                            key !== "CANCELLED" ||
                                                            project?.status === BRAND_PROJECT_STATUSES.CANCELLED
                                                    )
                                                    .map(([, value]) => ({
                                                        label: String(value),
                                                        value: String(value)
                                                    }))}
                                                required={false}
                                            />
                                        )}

                                        {isInternal(role) && isEdit && (
                                            <CustomSelectFieldLabel
                                                name="tdc_shipping_status"
                                                label="TDC Shipping Status"
                                                placeholder="Select TDC Shipping Status"
                                                options={Object.entries(BRAND_PROJECT_TDC_SHIPPING_STATUSES).map(
                                                    ([, value]) => ({
                                                        label: String(value),
                                                        value: String(value)
                                                    })
                                                )}
                                                required={false}
                                            />
                                        )}

                                        {isInternal(role) && isEdit && (
                                            <CustomSelectFieldLabel
                                                name="expert_shipping_status"
                                                label="Expert Shipping Status"
                                                placeholder="Select Expert Shipping Status"
                                                options={Object.entries(BRAND_PROJECT_EXPERT_SHIPPING_STATUSES).map(
                                                    ([, value]) => ({
                                                        label: String(value),
                                                        value: String(value)
                                                    })
                                                )}
                                                required={false}
                                            />
                                        )}

                                        {canEditDeliveryDate(role) && isEdit && (
                                            <CustomFieldCalendar
                                                name="earliest_delivery_date"
                                                label="Earliest Delivery Date"
                                                placeholder="Select earliest delivery date"
                                                minDate={toDay(project?.start_date) ?? undefined}
                                            />
                                        )}

                                        {isInternal(role) && isEdit && <DownloadPageToggle name="download_page" />}

                                        {isInternal(role) && isEdit && <ExpertsToggle name="experts" />}

                                        {(() => {
                                            const shouldShowRmnField =
                                                isInternal(role) ||
                                                isRMN(user) ||
                                                (isBrand(role) && !isRMN(user) && rmnBrandOptions.length > 0);

                                            if (!shouldShowRmnField) return null;

                                            const options = rmnBrandOptions;
                                            let isRequired = false;
                                            let isDisabled = false;
                                            let tooltipText =
                                                "Select the correct associated Parent from the drop down menu.";

                                            if (isInternal(role)) {
                                                isRequired = false;
                                            } else if (isRMN(user)) {
                                                isRequired = true;
                                                isDisabled = true;
                                                tooltipText =
                                                    "Your brand is a Parent and is pre-selected.";
                                            } else {
                                                isRequired = false;
                                            }

                                            return (
                                                <div>
                                                    <div className="flex items-center gap-[6px]">
                                                        <CustomFieldLabel
                                                            required={
                                                                isRequired
                                                            }
                                                        >
                                                            Parent
                                                        </CustomFieldLabel>
                                                        <BubbleInfo
                                                            tooltipText={
                                                                tooltipText
                                                            }
                                                        />
                                                    </div>
                                                    <div className="relative group">
                                                        <CustomSelectField
                                                            name="rmn"
                                                            label=""
                                                            placeholder="Select Parent"
                                                            options={options}
                                                            required={isRequired}
                                                            noLabel
                                                            disabled={isDisabled}
                                                            onChange={handleRMNChange}
                                                        />
                                                        {values.rmn && !isDisabled && (
                                                            <IconButton
                                                                onClick={() => {
                                                                    setFieldValue("rmn", "");
                                                                    setSelectedRmnBrand(null);
                                                                    setFieldValue("content_type_id", "");
                                                                    setFieldValue("content_channel_id", "");
                                                                    setSelectedContentTypeId(null);
                                                                }}
                                                                size="small"
                                                                sx={{
                                                                    position: "absolute",
                                                                    right: "32px",
                                                                    top: "50%",
                                                                    transform: "translateY(-50%)",
                                                                    color: "var(--text-2)",
                                                                    "&:hover": {
                                                                        backgroundColor: "var(--surface-2)"
                                                                    },
                                                                    opacity: 1
                                                                }}
                                                            >
                                                                <CloseIcon fontSize="small" />
                                                            </IconButton>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                        {(isInternal(role) || isRMN(user)) && (
                                            <div>
                                                <div className="flex items-center gap-[6px]">
                                                    <CustomFieldLabel required>Brand</CustomFieldLabel>
                                                    <BubbleInfo tooltipText="Select the Brand from the drop down menu or create a new Brand by typing in the name of the new Brand" />
                                                </div>
                                                <InlineBrandSelector
                                                    setFieldValue={setFieldValue}
                                                    isRMN={false}
                                                    selectedBrand={selectedBrand}
                                                    setSelectedBrand={(brand: any) => {
                                                        setSelectedBrand(brand);
                                                        setFieldValue("brand", brand?.name);
                                                        if (!brand || brand.id !== selectedBrand?.id) {
                                                            setSelectedProduct(null);
                                                            setFieldValue("product_name", "");
                                                            setFieldValue("product_description", "");
                                                            setFieldValue("brand_name", "");
                                                            setFieldValue("brand_description", "");
                                                        }
                                                    }}
                                                    disabled={false}
                                                    filterRmn={false}
                                                    allowAdd={isInternal(role) || isRMN(user)}
                                                />
                                            </div>
                                        )}

                                        <div>
                                            <div className="flex items-center gap-[6px]">
                                                <CustomFieldLabel required disabled={false}>
                                                    Product
                                                </CustomFieldLabel>
                                                <BubbleInfo tooltipText="Select the Product from the drop down menu or create a new Product by typing in the name of the new Product" />
                                            </div>
                                            {(() => {
                                                const nonRmnBrand = user?.brand?.find((b: any) => !b?.rmn);
                                                const selectedBrandId =
                                                    // eslint-disable-next-line no-nested-ternary
                                                    !isInternal(role) && nonRmnBrand?.id
                                                        ? nonRmnBrand.id
                                                        : selectedBrand && selectedBrand.id !== "new"
                                                        ? selectedBrand.id
                                                        : null;
                                                return (
                                                    <InlineProductSelector
                                                        setFieldValue={setFieldValue}
                                                        selectedProduct={selectedProduct}
                                                        setSelectedProduct={setSelectedProduct}
                                                        selectedBrandId={selectedBrandId}
                                                        selectedBrandName={selectedBrand?.name}
                                                        disabled={!selectedBrandId || isProcessLocked}
                                                        multiple={true}
                                                        selectedProducts={selectedProducts}
                                                        onProductsChange={setSelectedProducts}
                                                    />
                                                );
                                            })()}
                                        </div>

                                        {contentTypesMemo}

                                        {channelsMemo}

                                        {isInternal(role) && ordersMemo}

                                        {isInternal(role) && (
                                            <SlackChannelPicker
                                                name="slack_channel"
                                                brand={
                                                    selectedBrand
                                                        ? {
                                                              name: selectedBrand.name,
                                                              rmn: !!selectedBrand.rmn,
                                                              slack_channel: selectedBrand.slack_channel,
                                                          }
                                                        : null
                                                }
                                                hideCreate
                                            />
                                        )}

                                        {isInternal(role) && isEdit && (
                                            <>
                                                <InputField
                                                    name="client_feedback_tracker"
                                                    label="Client Feedback Tracker"
                                                    placeholder="Add notes or paste links to client feedback..."
                                                    required={false}
                                                />

                                                <InputField
                                                    name="est_time_set"
                                                    label="Est. Time on Set (hours)"
                                                    placeholder="Est. time in hrs..."
                                                    required={false}
                                                    type="number"
                                                />

                                                <Box>
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            mb: 1.5,
                                                            fontWeight: 800,
                                                            "&.MuiTypography-root": {
                                                                fontWeight: 800
                                                            }
                                                        }}
                                                    >
                                                        Shoot Location
                                                    </Typography>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12} sm={6}>
                                                            <InputField
                                                                name="address"
                                                                label="Address"
                                                                placeholder="Address"
                                                                required={false}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <InputField
                                                                name="address_2"
                                                                label="Address 2"
                                                                placeholder="Address 2"
                                                                required={false}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <InputField
                                                                name="city"
                                                                label="City"
                                                                placeholder="City"
                                                                required={false}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <CustomSelectField
                                                                name="state"
                                                                label="State"
                                                                placeholder="State"
                                                                required={false}
                                                                options={statesList}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <InputField
                                                                name="zip"
                                                                label="Zip"
                                                                placeholder="Zip"
                                                                required={false}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </>
                                        )}

                                        <ProjectQuestionToggle
                                            label="Is this product available online for purchase?"
                                            value={onlinePurchase}
                                            onChange={setOnlinePurchase}
                                        />

                                        <ProjectQuestionToggle
                                            label="Are you able to ship at least two of these products?"
                                            value={canShipTwo}
                                            onChange={(val) => {
                                                setCanShipTwo(val);
                                                scrollToBottom();
                                            }}
                                        />

                                        <ProjectQuestionToggle
                                            label="Is the product under 100lbs?"
                                            value={isOver100Lbs}
                                            onChange={(val) => {
                                                setIsOver100Lbs(val);
                                                scrollToBottom();
                                            }}
                                        />

                                        {(!canShipTwo || !isOver100Lbs) && (
                                            <Box className="mt-1 p-3 bg-blue-50 border border-blue-200 rounded-md">
                                                <Typography variant="body2" className="text-blue-800">
                                                    Your account manager will contact you to arrange product shipment.
                                                </Typography>
                                            </Box>
                                        )}

                                        {/* Advanced Video Options Section */}
                                        {videoOptions.length > 0 && (
                                            <Accordion
                                                sx={{
                                                    boxShadow: "none",
                                                    border: "1px solid var(--border)",
                                                    borderRadius: "8px !important",
                                                    "&:before": {
                                                        display: "none"
                                                    },
                                                    "&.Mui-expanded": {
                                                        margin: 0
                                                    }
                                                }}
                                            >
                                                <AccordionSummary
                                                    expandIcon={<ExpandMoreIcon />}
                                                    sx={{ minHeight: "48px" }}
                                                >
                                                    <Typography fontWeight={600}>Advanced Options</Typography>
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    {videoOptionsLoading ? (
                                                        <Box display="flex" justifyContent="center" py={2}>
                                                            <CircularProgress size={24} />
                                                        </Box>
                                                    ) : (
                                                        <Box display="flex" flexDirection="column" gap={2}>
                                                            {videoOptions.map((option) => {
                                                                const optionType = option.type || "additive";

                                                                // Render based on option type
                                                                if (optionType === "value") {
                                                                    // Value type: numeric inputs for each value
                                                                    const valueRecord =
                                                                        (videoOptionSelections[option.id] as Record<
                                                                            number,
                                                                            string | number
                                                                        >) || {};
                                                                    return (
                                                                        <Box key={option.id}>
                                                                            <Typography
                                                                                variant="body2"
                                                                                fontWeight={500}
                                                                                sx={{
                                                                                    mb: 1,
                                                                                    color: "var(--text-2)"
                                                                                }}
                                                                            >
                                                                                {option.name}
                                                                            </Typography>
                                                                            <Box
                                                                                display="flex"
                                                                                flexDirection="column"
                                                                                gap={1.5}
                                                                            >
                                                                                {option.values.map((value) => (
                                                                                    <Box
                                                                                        key={value.id}
                                                                                        display="flex"
                                                                                        alignItems="center"
                                                                                        gap={2}
                                                                                    >
                                                                                        <Typography
                                                                                            variant="body2"
                                                                                            sx={{
                                                                                                minWidth: "80px",
                                                                                                color: "var(--text)"
                                                                                            }}
                                                                                        >
                                                                                            {value.name}:
                                                                                        </Typography>
                                                                                        <TextField
                                                                                            type="number"
                                                                                            size="small"
                                                                                            value={
                                                                                                valueRecord[value.id] ??
                                                                                                ""
                                                                                            }
                                                                                            onChange={(e) =>
                                                                                                updateValueOption(
                                                                                                    option.id,
                                                                                                    value.id,
                                                                                                    e.target.value
                                                                                                )
                                                                                            }
                                                                                            sx={{
                                                                                                width: "150px"
                                                                                            }}
                                                                                            inputProps={{
                                                                                                style: {
                                                                                                    textAlign: "center"
                                                                                                }
                                                                                            }}
                                                                                        />
                                                                                    </Box>
                                                                                ))}
                                                                            </Box>
                                                                        </Box>
                                                                    );
                                                                }

                                                                if (optionType === "exclusive") {
                                                                    // Exclusive type: radio buttons
                                                                    const selectedSet =
                                                                        (videoOptionSelections[
                                                                            option.id
                                                                        ] as Set<number>) || new Set();
                                                                    const selectedValue =
                                                                        selectedSet.size > 0
                                                                            ? Array.from(selectedSet)[0]
                                                                            : "";
                                                                    return (
                                                                        <Box key={option.id}>
                                                                            <Typography
                                                                                variant="body2"
                                                                                fontWeight={500}
                                                                                sx={{
                                                                                    mb: 1,
                                                                                    color: "var(--text-2)"
                                                                                }}
                                                                            >
                                                                                {option.name}
                                                                            </Typography>
                                                                            <RadioGroup
                                                                                row
                                                                                value={selectedValue}
                                                                                onChange={(e) =>
                                                                                    selectExclusive(
                                                                                        option.id,
                                                                                        parseInt(e.target.value)
                                                                                    )
                                                                                }
                                                                            >
                                                                                {option.values.map((value) => (
                                                                                    <FormControlLabel
                                                                                        key={value.id}
                                                                                        value={value.id}
                                                                                        control={
                                                                                            <Radio
                                                                                                size="small"
                                                                                                sx={{
                                                                                                    "&.Mui-checked": {
                                                                                                        color: "primary.main"
                                                                                                    }
                                                                                                }}
                                                                                            />
                                                                                        }
                                                                                        label={value.name}
                                                                                        sx={{
                                                                                            "& .MuiFormControlLabel-label":
                                                                                                {
                                                                                                    fontSize: "14px"
                                                                                                }
                                                                                        }}
                                                                                    />
                                                                                ))}
                                                                            </RadioGroup>
                                                                        </Box>
                                                                    );
                                                                }

                                                                // Default: additive type - multi-select chips
                                                                const selectedValues =
                                                                    (videoOptionSelections[option.id] as Set<number>) ||
                                                                    new Set();
                                                                return (
                                                                    <Box key={option.id}>
                                                                        <Typography
                                                                            variant="body2"
                                                                            fontWeight={500}
                                                                            sx={{
                                                                                mb: 1,
                                                                                color: "var(--text-2)"
                                                                            }}
                                                                        >
                                                                            {option.name}
                                                                        </Typography>
                                                                        <Box display="flex" flexWrap="wrap" gap={1}>
                                                                            {option.values.map((value) => {
                                                                                const isSelected = selectedValues.has(
                                                                                    value.id
                                                                                );
                                                                                return (
                                                                                    <Chip
                                                                                        key={value.id}
                                                                                        label={value.name}
                                                                                        onClick={() =>
                                                                                            toggleAdditive(
                                                                                                option.id,
                                                                                                value.id
                                                                                            )
                                                                                        }
                                                                                        variant={
                                                                                            isSelected
                                                                                                ? "filled"
                                                                                                : "outlined"
                                                                                        }
                                                                                        color={
                                                                                            isSelected
                                                                                                ? "primary"
                                                                                                : "default"
                                                                                        }
                                                                                        sx={{
                                                                                            cursor: "pointer",
                                                                                            "&:hover": {
                                                                                                backgroundColor:
                                                                                                    isSelected
                                                                                                        ? undefined
                                                                                                        : "var(--surface-2)"
                                                                                            }
                                                                                        }}
                                                                                    />
                                                                                );
                                                                            })}
                                                                        </Box>
                                                                    </Box>
                                                                );
                                                            })}
                                                        </Box>
                                                    )}
                                                </AccordionDetails>
                                            </Accordion>
                                        )}

                                        <Accordion
                                            sx={{
                                                boxShadow: "none",
                                                border: "1px solid var(--border)",
                                                borderRadius: "12px !important",
                                                mt: 2,
                                                "&:before": { display: "none" }
                                            }}
                                        >
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                <Typography
                                                    sx={{
                                                        fontFamily: "CircularStd",
                                                        fontWeight: 700,
                                                        fontSize: "15px",
                                                        color: "var(--text)"
                                                    }}
                                                >
                                                    License picker
                                                </Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <CustomSelectField
                                                    name="license_key"
                                                    label="License"
                                                    placeholder="No license"
                                                    options={[
                                                        { label: "No license", value: "" },
                                                        ...licenseOptions.map((l) => ({
                                                            label: `${l.display_name} — ${formatCurrency(Number(l.price))} per video`,
                                                            value: l.key
                                                        }))
                                                    ]}
                                                    onChange={(e: any) =>
                                                        setFieldValue("license_key", e.target.value || null)
                                                    }
                                                />
                                            </AccordionDetails>
                                        </Accordion>

                                        {isInternal(role) && isEdit && (
                                            <>
                                                <CustomSelectField
                                                    name="account_manager_id"
                                                    label="Account Manager"
                                                    placeholder={staffPlaceholder(
                                                        accountManagers,
                                                        "Select Account Manager"
                                                    )}
                                                    required={false}
                                                    disabled={accountManagers.loading || accountManagers.error}
                                                    options={accountManagers.options}
                                                />
                                                <CustomSelectField
                                                    name="producer_id"
                                                    label="Producer"
                                                    placeholder={staffPlaceholder(fulfillment, "Select Producer")}
                                                    required={false}
                                                    disabled={fulfillment.loading || fulfillment.error}
                                                    options={fulfillment.options}
                                                />
                                                <CustomSelectField
                                                    name="project_manager_id"
                                                    label="Project Manager"
                                                    placeholder={staffPlaceholder(projectManagers, "Select Project Manager")}
                                                    required={false}
                                                    disabled={projectManagers.loading || projectManagers.error}
                                                    options={projectManagers.options}
                                                />
                                                <CustomSelectField
                                                    name="editor_id"
                                                    label="Editor"
                                                    placeholder={staffPlaceholder(fulfillment, "Select Editor")}
                                                    required={false}
                                                    disabled={fulfillment.loading || fulfillment.error}
                                                    options={fulfillment.options}
                                                />
                                            </>
                                        )}
                                    </Box>
                                </DialogContent>
                            </Form>

                            <DialogActions className="!px-6 !py-4">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    disabled={isProcessingQueue}
                                    onClick={submitForm}
                                    sx={{
                                        alignSelf: "flex-end",
                                        textTransform: "capitalize"
                                    }}
                                    startIcon={
                                        isProcessingQueue ? <CircularProgress size={16} color="inherit" /> : undefined
                                    }
                                >
                                    {isEdit ? "Save Changes" : "Create Project"}
                                </Button>
                            </DialogActions>
                        </>
                    );
                }}
            </Formik>

            <ConfirmationDialog
                open={!!pendingRevert}
                onClose={() => setPendingRevert(null)}
                onConfirm={() => {
                    const pending = pendingRevert;
                    setPendingRevert(null);
                    if (pending) handleSubmit(pending.values, pending.actions, true);
                }}
                maxWidth="xs"
                title="Revert expert selection?"
                description={[
                    `This project's expert selection is locked (Pre-Production reached). Moving it back to ${
                        pendingRevert?.values.status
                    } reverts the whole selection:`,
                    `Every project on the selection moves to ${pendingRevert?.values.status}, the expert SOWs already sent are voided, and film days and scheduling are cleared. Cancelled projects keep their status.`
                ]}
                confirmLabel="Revert selection"
            />
        </Dialog>
    );
}
